import type { BookingExecutionMode, BookingSelectedTime, WaitingListSelection } from '@/types/booking-flow';
import type { Service } from '@/types/domain/service';
import { resolveCompanyBanner } from '@/lib/image-placeholders';
import { Calendar, Clock, Scissors, Star, User } from 'lucide-react';

interface BookingSummaryStepProps {
  companyCoverPhoto?: string;
  companyName: string;
  companyRating?: number;
  companyStreet?: string;
  executionMode?: BookingExecutionMode;
  proName: string;
  selectedServiceItems: Service[];
  selectedTime: BookingSelectedTime | null;
  totalDuration: string;
  totalPrice: number;
  waitingListSelection?: WaitingListSelection | null;
}

const BookingSummaryStep = ({
  companyCoverPhoto,
  companyName,
  companyRating,
  companyStreet,
  executionMode = 'booking',
  proName,
  selectedServiceItems,
  selectedTime,
  totalDuration,
  totalPrice,
  waitingListSelection,
}: BookingSummaryStepProps) => {
  const isWaitingList = executionMode === 'waiting-list';
  const resolvedDateLine = isWaitingList
    ? waitingListSelection
      ? `${waitingListSelection.dayOfWeek} (${waitingListSelection.date})`
      : 'Data não selecionada'
    : `${selectedTime?.label} (${selectedTime?.date}) às ${selectedTime?.time}`;
  const resolvedTimeLine = waitingListSelection?.preferredTime
    ? `Se abrir às ${waitingListSelection.preferredTime}, a gente te avisa`
    : 'Qualquer horário desse dia';

  return (
    <div className="animate-fade-in-up space-y-5">
      <div className="border-border bg-card space-y-4 rounded-xl border p-5">
        <h3 className="text-card-foreground text-lg font-bold">
          {isWaitingList ? 'Resumo da fila de espera' : 'Resumo do agendamento'}
        </h3>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Scissors className="text-primary mt-1 h-4 w-4 shrink-0" />
            <div>
              <p className="text-card-foreground text-sm font-medium">Serviços</p>
              {selectedServiceItems.map((service) => (
                <p key={service.id} className="text-muted-foreground text-sm">
                  {service.name} — R$ {service.price.toFixed(2).replace('.', ',')}
                </p>
              ))}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <User className="text-primary mt-1 h-4 w-4 shrink-0" />
            <div>
              <p className="text-card-foreground text-sm font-medium">Profissional</p>
              <p className="text-muted-foreground text-sm">{proName}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="text-primary mt-1 h-4 w-4 shrink-0" />
            <div>
              <p className="text-card-foreground text-sm font-medium">
                {isWaitingList ? 'Dia desejado' : 'Data e horário'}
              </p>
              <p className="text-muted-foreground text-sm">{resolvedDateLine}</p>
              {isWaitingList && <p className="text-muted-foreground mt-1 text-sm">{resolvedTimeLine}</p>}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="text-primary mt-1 h-4 w-4 shrink-0" />
            <div>
              <p className="text-card-foreground text-sm font-medium">Duração estimada</p>
              <p className="text-muted-foreground text-sm">{totalDuration}</p>
            </div>
          </div>
        </div>

        <div className="border-border flex items-center justify-between border-t pt-3">
          <span className="text-card-foreground font-semibold">{isWaitingList ? 'Estimativa' : 'Total'}</span>
          <span className="text-primary text-lg font-bold">R$ {totalPrice.toFixed(2).replace('.', ',')}</span>
        </div>
      </div>

      <div className="border-border bg-card flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center">
        <img
          src={resolveCompanyBanner(companyCoverPhoto)}
          alt={companyName}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
        />
        <div className="min-w-0">
          <p className="text-card-foreground text-sm font-semibold">{companyName}</p>
          <p className="text-muted-foreground truncate text-xs">{companyStreet}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1 sm:ml-auto">
          <Star className="fill-warning text-warning h-3.5 w-3.5" />
          <span className="text-card-foreground text-sm font-medium">{companyRating}</span>
        </div>
      </div>
    </div>
  );
};

export default BookingSummaryStep;
